import React from 'react'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import Head from 'next/head'
import utc from 'dayjs/plugin/utc'
import { useRouter } from 'next/router'

import 'dayjs/locale/id'
import { Text } from '@wartek-id/text'

import { useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import WebinarCard, { WebinarPreloader } from '../partials/Webinar/WebinarCard'
import { MILKvKeys } from 'api/microlearning/useAPIKeyValue'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { getWebinarTopicData } from '../utils/webinar'
import { useGetTopicList } from 'api/microlearning/useAPITopics'
import { useSafeRouter } from 'hooks/useSafeRouter'

import type { WebinarEntry } from 'api/microlearning/types'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('id')

const minutesBefore = 30
const WebinarList = ({ isDesktop = false, currentId = '' }) => {
  const { data, status } = useGetKeyPairValue(MILKvKeys.WebinarEntries)
  const { data: topics } = useGetTopicList()
  const webinarListRef = React.useRef<HTMLDivElement>(null)
  const wibTime = dayjs().tz('Asia/Jakarta')
  const router = useRouter()
  const { safeReplace } = useSafeRouter()

  const RenderWebinarList = () => (
    <div className="px-4 pt-4" ref={webinarListRef}>
      {status === 'loading' &&
        [...Array(4)].map((_arr, index) => (
          <div key={`webinar-preloader-${index}`} className="mb-4">
            <WebinarPreloader />
          </div>
        ))}
      {status === 'success' &&
        !!data?.data?.webinar &&
        data.data.webinar.map(
          ({
            id,
            title,
            topic,
            speakers,
            startDate,
            endDate,
            module,
          }: WebinarEntry) => {
            const topicData = getWebinarTopicData(
              {
                topikId: topic?.id,
                topikTitle: topic?.title,
              },
              (topics?.data || []).map(({ id }) => +id),
              module
            )

            if (wibTime.isBefore(dayjs(endDate).add(minutesBefore, 'minute'))) {
              return (
                <div key={`webinar-card-${id}`} className="mb-4">
                  <WebinarCard
                    id={id}
                    href={`/${
                      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                      'pelatihan-mandiri'
                    }/webinar/${id}`}
                    topicId={`${topicData?.topikId}`}
                    topicName={topicData?.topikTitle}
                    title={title}
                    speakers={speakers}
                    startDate={startDate}
                    endDate={endDate}
                    active={currentId === id}
                  />
                </div>
              )
            }

            return null
          }
        )}
      {status === 'success' &&
        data.data?.webinar?.filter(({ endDate }) =>
          wibTime.isBefore(dayjs(endDate).add(minutesBefore, 'minute'))
        ) <= 0 && (
          <EmptyState
            title="Tidak ada Webinar"
            description="Belum ada Webinar yang akan berlangsung untuk saat ini, silakan coba akses halaman ini lagi di lain waktu"
          >
            <EmptyStateAction onClick={() => router.back()}>
              Kembali
            </EmptyStateAction>
          </EmptyState>
        )}
    </div>
  )

  if (isDesktop) {
    if (!currentId) {
      const firstWebinarChildInRef = webinarListRef.current?.children[0]

      if (currentId === '' && firstWebinarChildInRef) {
        // find and get href in a tag
        const firstWebinarChildHref = firstWebinarChildInRef
          .querySelector('a')
          ?.getAttribute('href')

        if (firstWebinarChildHref) {
          safeReplace(firstWebinarChildHref)
        }
      }
    }

    return (
      <>
        <Head>
          <title>Daftar Webinar</title>
        </Head>
        <div className="mt-[80px]">
          <Text variant="display-sm" className="p-4">
            Daftar Webinar
          </Text>
          <RenderWebinarList />
        </div>
      </>
    )
  }
  return (
    <>
      <Head>
        <title>Daftar Webinar</title>
      </Head>
      <div
        className="overflow-y-auto"
        style={{ height: 'calc(100vh - 3.5rem)' }}
      >
        <RenderWebinarList />
      </div>
    </>
  )
}

export default WebinarList
